import {
    ESSENCE_RING_COSTS,
    ESSENCE_RING_MAX,
    resolveEssenceExchangeValue,
    resolveEssenceRing,
    resolveRespecCost,
    resolveSkillPointsAvailable,
    resolveSkillPointsEarned,
    resolveVeilAttunementPercent,
} from '@sorrem/shared';
import { useCallback } from 'react';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { useRaiseEssenceSkillMutation, useRespecEssenceSkillsMutation } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useMutationError } from './use-mutation-error';

export interface EssenceSkillPointsState {
    /** Veil Attunement, 0 to 100. */
    veilAttunementPercent: number;
    earnedPoints: number;
    availablePoints: number;
    /** Banked essence value the next skill-point respec costs. */
    respecCost: number;
    /** Banked essence value the character has. */
    bankedValue: number;
    isSaving: boolean;
    /** Why the last raise or respec failed, or null. */
    error: string | null;
    /** Points the next ring of `skillId` costs, or null once it's mastered. */
    nextRingCost: (skillId: string) => number | null;
    /** Raises an Essence skill one ring. Resolves to whether it succeeded. */
    raise: (skillId: string) => Promise<boolean>;
    /** Returns every point, for banked essence, to place again. Resolves to whether it succeeded. */
    resetPoints: () => Promise<boolean>;
}

/** Spending the skill points Veil Attunement grants on Essence skills, ring by ring (#145). */
export function useEssenceSkillPoints(character: UIPartyCharacter): EssenceSkillPointsState {
    const accountId = getStoredCanonicalAccountId();
    const [raiseSkill, { isLoading: isRaising }] = useRaiseEssenceSkillMutation();
    const [respecSkills, { isLoading: isRespeccing }] = useRespecEssenceSkillsMutation();
    const { error, refuse, run: runRequest } = useMutationError();

    const run = useCallback((request: (accountId: string) => Promise<unknown>): Promise<boolean> => (
        accountId === null
            ? refuse('You need to be signed in to spend skill points.')
            : runRequest(() => request(accountId), 'That didn\'t go through. Try again.')
    ), [accountId, refuse, runRequest]);

    const raise = useCallback((skillId: string) => {
        const skill = character.skills.find((candidate) => candidate.id === skillId);
        const ring = resolveEssenceRing(skill?.level ?? 1) + 1;
        return run((id) => raiseSkill({ accountId: id, characterId: character.id, skillId, ring }).unwrap());
    }, [character.id, character.skills, raiseSkill, run]);

    const resetPoints = useCallback(() => run(
        (id) => respecSkills({ accountId: id, characterId: character.id, rings: {} }).unwrap(),
    ), [character.id, respecSkills, run]);

    const nextRingCost = useCallback((skillId: string): number | null => {
        const skill = character.skills.find((candidate) => candidate.id === skillId);
        const nextRing = resolveEssenceRing(skill?.level ?? 1) + 1;
        return nextRing > ESSENCE_RING_MAX ? null : ESSENCE_RING_COSTS[nextRing] ?? null;
    }, [character.skills]);

    const veilAttunementPercent = resolveVeilAttunementPercent(character.veilAttunementSpent);
    return {
        veilAttunementPercent,
        earnedPoints: resolveSkillPointsEarned(veilAttunementPercent),
        availablePoints: resolveSkillPointsAvailable(character.veilAttunementSpent, character.skills),
        respecCost: resolveRespecCost(character.skillRespecCount),
        bankedValue: resolveEssenceExchangeValue(character.permanentEssence),
        isSaving: isRaising || isRespeccing,
        error,
        nextRingCost,
        raise,
        resetPoints,
    };
}
